import { Fragment, useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api, listResource } from '@/lib/api';
import { customFieldFormSpan, customFieldUsesFullWidth } from '@/lib/custom-field-form-order';
import { hierarchyLevelLabel, roleHierarchyLevel, roleLabel, sortRolesByHierarchy } from '@/lib/role-display';
import { customFieldOrderSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import {
  UNGROUPED_FORM_FIELDS,
  fallbackUserFormLayout,
  formColumnSpan,
  formGroupIcon,
  isUserFormBuiltin,
  layoutGroupsForExtra,
  layoutItemsForSection,
  moveUserFormLayoutItem,
  sameUserFormLayout,
  userFormLayoutGaps,
  type UserFormLayoutItem,
  type UserFormSectionId,
} from '@/lib/user-form-layout';
import { CustomFieldPreviewField } from './CustomFieldPreviewField';

interface OrderField {
  id: string;
  name: string;
  slug: string;
  type: string;
  placeholder?: string | null;
  helpText?: string | null;
  required?: boolean;
  options?: Array<{ label: string; value: string; isActive?: boolean }>;
  maxFiles?: number;
  optionsLayout?: 'vertical' | 'horizontal';
  formWidth?: 'half' | 'full' | null;
  groupName?: string;
  sortOrder?: number;
}

interface UserCustomFieldsFormData {
  personalFields: OrderField[];
  sections: { groupName: string; fields: OrderField[] }[];
  layout?: UserFormLayoutItem[];
}

interface RoleOption {
  id: string;
  name: string;
  label?: string | null;
  hierarchyLevel?: number;
}

const FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white px-4 py-3 text-base text-dash-on-surface outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

const LABEL = 'text-sm font-medium text-dash-on-surface-variant';

const BUILTIN_LABELS: Record<string, string> = {
  firstName: 'Nombre',
  lastName: 'Apellidos',
  email: 'Correo electrónico',
  phone: 'Teléfono',
  password: 'Contraseña',
  passwordConfirmation: 'Confirmar contraseña',
  status: 'Estado',
  roles: 'Roles asignados',
};

function SectionHeading({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="mb-6 flex items-center gap-2">
      <span className="material-symbols-outlined text-dash-primary" aria-hidden="true">
        {icon}
      </span>
      <h2 className="text-xl font-semibold text-dash-on-surface">{title}</h2>
    </div>
  );
}

function StaticText({
  id,
  label,
  required,
  type = 'text',
  placeholder,
}: {
  id: string;
  label: string;
  required?: boolean;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div className="space-y-2">
      <label htmlFor={id} className={LABEL}>
        {label} {required ? <span className="text-error-600">*</span> : null}
      </label>
      <input id={id} type={type} readOnly tabIndex={-1} placeholder={placeholder} className={FIELD} />
    </div>
  );
}

function BuiltinPreview({ id, roles }: { id: string; roles: RoleOption[] }) {
  switch (id) {
    case 'firstName':
      return <StaticText id="order-first-name" label="Nombre" required placeholder="Ej: Juan" />;
    case 'lastName':
      return <StaticText id="order-last-name" label="Apellidos" required placeholder="Ej: Pérez" />;
    case 'email':
      return (
        <StaticText
          id="order-email"
          label="Correo electrónico"
          required
          type="email"
          placeholder="juan.perez@academia.edu"
        />
      );
    case 'phone':
      return <StaticText id="order-phone" label="Teléfono" type="tel" placeholder="+56 9 1234 5678" />;
    case 'password':
      return <StaticText id="order-password" label="Contraseña" required type="password" />;
    case 'passwordConfirmation':
      return <StaticText id="order-password-confirmation" label="Confirmar contraseña" required type="password" />;
    case 'status':
      return (
        <div className="space-y-2">
          <label htmlFor="order-status" className={LABEL}>
            Estado
          </label>
          <select id="order-status" tabIndex={-1} defaultValue="active" className={`${FIELD} pointer-events-none`}>
            <option value="active">Activo</option>
            <option value="inactive">Inactivo</option>
          </select>
        </div>
      );
    case 'roles':
      return (
        <div className="space-y-4">
          <span className={LABEL}>
            Roles asignados <span className="text-error-600">*</span>
          </span>
          <div className="flex flex-wrap gap-3">
            {roles.map((role) => (
              <span
                key={role.id}
                className="inline-flex items-center gap-2 rounded-full border border-dash-outline-variant px-4 py-2"
              >
                <span className="text-sm font-medium">
                  {roleLabel(role.name, role.label)} (
                  {hierarchyLevelLabel(role.hierarchyLevel ?? roleHierarchyLevel(role.name))})
                </span>
              </span>
            ))}
          </div>
        </div>
      );
    default:
      return null;
  }
}

function DraggableSlot({
  id,
  label,
  list,
  span,
  full,
  draggingId,
  overId,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onToggleWidth,
  children,
}: {
  id: string;
  label: string;
  list: UserFormSectionId;
  span: string;
  full: boolean;
  draggingId: string | null;
  overId: string | null;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onDragOver: (id: string) => void;
  onDrop: (id: string, list: UserFormSectionId, activeId: string) => void;
  onToggleWidth: (id: string) => void;
  children: ReactNode;
}) {
  const active = draggingId === id;
  const target = overId === id && draggingId !== id;

  return (
    <div
      draggable
      onDragStart={(event) => {
        if ((event.target as HTMLElement).closest('[data-width-toggle]')) {
          event.preventDefault();
          return;
        }
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', id);
        onDragStart(id);
      }}
      onDragEnd={onDragEnd}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        onDragOver(id);
      }}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onDrop(id, list, event.dataTransfer.getData('text/plain'));
      }}
      className={`group relative min-w-0 cursor-grab rounded-lg active:cursor-grabbing ${span} ${active ? 'opacity-40' : ''} ${
        target ? 'ring-2 ring-dash-primary ring-offset-2' : ''
      }`}
      aria-grabbed={active}
      title="Arrastrar para ordenar"
    >
      <div className="absolute top-0 right-0 z-10 flex items-center gap-1">
        <button
          type="button"
          data-width-toggle
          draggable={false}
          className="rounded-md border border-dash-outline-variant bg-white px-2 py-0.5 text-xs font-medium text-dash-primary shadow-sm hover:bg-secondary-50"
          aria-label={`${label}: ${full ? 'largo, ocupa toda la fila' : 'mitad de la fila'}. Cambiar ancho`}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onToggleWidth(id);
          }}
        >
          {full ? 'Largo' : 'Mitad'}
        </button>
        <span
          className="material-symbols-outlined pointer-events-none text-dash-on-surface-variant opacity-40 group-hover:opacity-100"
          aria-hidden="true"
        >
          drag_indicator
        </span>
      </div>
      <div className="pointer-events-none [&>*:first-child>:first-child]:pr-28">{children}</div>
    </div>
  );
}

function DropGap({
  gapId,
  wide,
  active,
  onDragOver,
  onDrop,
}: {
  gapId: string;
  wide: 'md' | 'lg';
  active: boolean;
  onDragOver: (gapId: string) => void;
  onDrop: (gapId: string, activeId: string) => void;
}) {
  const visibility = wide === 'md' ? 'hidden md:flex' : 'hidden lg:flex';
  return (
    <div
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        onDragOver(gapId);
      }}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onDrop(gapId, event.dataTransfer.getData('text/plain'));
      }}
      className={`${visibility} min-h-28 min-w-0 items-center justify-center self-stretch rounded-lg border border-dashed px-4 text-center text-sm ${
        active
          ? 'border-dash-primary bg-secondary-50 text-dash-primary'
          : 'border-dash-outline-variant text-dash-on-surface-variant'
      }`}
    >
      Suelte aquí
    </div>
  );
}

export function UserCustomFieldFormOrderPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const dragRef = useRef<string | null>(null);
  const committedRef = useRef(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [overGapId, setOverGapId] = useState<string | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['user-custom-fields', 'form'],
    queryFn: () => api<UserCustomFieldsFormData>('/user-custom-fields/form'),
  });
  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: () => listResource<RoleOption>('/roles'),
  });

  const customFields = useMemo(() => {
    const personal = data?.personalFields ?? [];
    const extra = (data?.sections ?? []).flatMap((section) =>
      section.fields.map((field) => ({ ...field, groupName: field.groupName || section.groupName })),
    );
    return [...personal, ...extra];
  }, [data?.personalFields, data?.sections]);
  const fieldById = useMemo(() => new Map(customFields.map((field) => [field.id, field])), [customFields]);
  const roles = useMemo(() => sortRolesByHierarchy(rolesData?.data ?? []), [rolesData?.data]);
  const savedItems = useMemo(() => {
    if (data?.layout?.length) return data.layout;
    const personal = (data?.personalFields ?? []).map((field) => ({
      id: field.id,
      formWidth: customFieldUsesFullWidth(field) ? ('full' as const) : ('half' as const),
    }));
    const extra = (data?.sections ?? [])
      .flatMap((section) => section.fields)
      .map((field) => ({
        id: field.id,
        formWidth: customFieldUsesFullWidth(field) ? ('full' as const) : ('half' as const),
      }));
    return fallbackUserFormLayout(personal, extra);
  }, [data?.layout, data?.personalFields, data?.sections]);

  const [items, setItems] = useState<UserFormLayoutItem[] | null>(null);

  useEffect(() => {
    if (!data) return;
    setItems(savedItems);
  }, [data, savedItems]);

  const layout = items ?? savedItems;
  const dirty = !sameUserFormLayout(layout, savedItems);
  const extraGroups = useMemo(
    () =>
      layoutGroupsForExtra(
        layout,
        (id) => fieldById.get(id)?.groupName,
        (data?.sections ?? []).map((section) => section.groupName),
      ),
    [layout, fieldById, data?.sections],
  );

  function groupNameOf(id: string) {
    return fieldById.get(id)?.groupName?.trim() || UNGROUPED_FORM_FIELDS;
  }

  function canDropInExtraGroup(activeId: string, extraGroup: string) {
    if (!fieldById.has(activeId)) return extraGroup === UNGROUPED_FORM_FIELDS;
    return groupNameOf(activeId) === extraGroup;
  }

  function startDrag(id: string) {
    committedRef.current = false;
    dragRef.current = id;
    setDraggingId(id);
    setOverId(null);
    setOverGapId(null);
  }

  function endDrag() {
    dragRef.current = null;
    setDraggingId(null);
    setOverId(null);
    setOverGapId(null);
  }

  function releaseDrag() {
    window.setTimeout(() => {
      if (committedRef.current) return;
      endDrag();
    }, 0);
  }

  function hoverDrag(id: string) {
    const active = dragRef.current;
    if (!active || active === id) return;
    const target = layout.find((item) => item.id === id);
    if (target?.section === 'extra' && !canDropInExtraGroup(active, groupNameOf(id))) {
      setOverId(null);
      return;
    }
    setOverGapId(null);
    setOverId(id);
  }

  function hoverGap(gapId: string, extraGroup?: string) {
    if (!dragRef.current) return;
    if (extraGroup && !canDropInExtraGroup(dragRef.current, extraGroup)) {
      setOverGapId(null);
      return;
    }
    setOverId(null);
    setOverGapId(gapId);
  }

  function dropDrag(id: string, section: UserFormSectionId, activeId: string) {
    const active = activeId || dragRef.current;
    if (committedRef.current) {
      endDrag();
      return;
    }
    committedRef.current = true;
    endDrag();
    if (!active || active === id) return;
    if (section === 'extra' && !canDropInExtraGroup(active, groupNameOf(id))) return;
    setItems((current) => moveUserFormLayoutItem(current ?? savedItems, active, id, section, 'slot'));
  }

  function dropOnSection(section: UserFormSectionId, activeId: string, extraGroup?: string) {
    const active = activeId || dragRef.current;
    if (committedRef.current) {
      endDrag();
      return;
    }
    committedRef.current = true;
    endDrag();
    if (!active) return;
    if (extraGroup && !canDropInExtraGroup(active, extraGroup)) {
      if (!fieldById.has(active)) return;
      setItems((current) => moveUserFormLayoutItem(current ?? savedItems, active, null, 'extra'));
      return;
    }
    setItems((current) => moveUserFormLayoutItem(current ?? savedItems, active, null, section));
  }

  function dropOnGap(section: UserFormSectionId, beforeId: string | null, activeId: string, extraGroup?: string) {
    const active = activeId || dragRef.current;
    if (committedRef.current) {
      endDrag();
      return;
    }
    committedRef.current = true;
    endDrag();
    if (!active || active === beforeId) return;
    if (extraGroup && !canDropInExtraGroup(active, extraGroup)) return;
    setItems((current) => moveUserFormLayoutItem(current ?? savedItems, active, beforeId, section));
  }

  function toggleWidth(id: string) {
    setItems((current) =>
      (current ?? savedItems).map((item) =>
        item.id === id ? { ...item, formWidth: item.formWidth === 'full' ? 'half' : 'full' } : item,
      ),
    );
  }

  const mutation = useMutation({
    mutationFn: () =>
      api('/user-custom-fields/order', {
        method: 'PATCH',
        body: { items: layout },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['user-custom-fields'] });
      toast(customFieldOrderSuccessToast());
    },
    onError: (err) => {
      toast(roleActionErrorToast(err, 'No se pudo guardar el orden del formulario'));
    },
  });

  const dragProps = {
    draggingId,
    overId,
    onDragStart: startDrag,
    onDragEnd: releaseDrag,
    onDragOver: hoverDrag,
    onDrop: dropDrag,
    onToggleWidth: toggleWidth,
  };

  function allowSectionDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }

  function renderSection(
    section: UserFormSectionId,
    icon: string,
    title: string,
    gridClass: string,
    wide: 'md' | 'lg',
    sectionItems = layoutItemsForSection(layout, section),
    extraGroup?: string,
  ) {
    const gaps = userFormLayoutGaps(sectionItems);
    const gapBefore = new Map(gaps.filter((gap) => gap.beforeId).map((gap) => [gap.beforeId, gap]));
    const endGap = gaps.find((gap) => gap.beforeId === null) ?? null;
    const gapScope = extraGroup ?? section;
    return (
      <section key={gapScope} className="border-b border-dash-outline-variant p-6 md:p-8">
        <SectionHeading icon={icon} title={title} />
        <div
          className={gridClass}
          onDragOver={allowSectionDrop}
          onDrop={(event) => {
            if (event.target !== event.currentTarget) return;
            event.preventDefault();
            dropOnSection(section, event.dataTransfer.getData('text/plain'), extraGroup);
          }}
        >
          {sectionItems.length === 0 ? (
            <p className="pointer-events-none col-span-full rounded-lg border border-dashed border-dash-outline-variant px-4 py-6 text-center text-sm text-dash-on-surface-variant">
              Suelte un campo aquí.
            </p>
          ) : null}
          {sectionItems.map((item) => {
            const field = fieldById.get(item.id);
            const label = field?.name ?? BUILTIN_LABELS[item.id] ?? 'Campo';
            const span = field
              ? (customFieldFormSpan({ ...field, formWidth: item.formWidth }, wide) ?? '')
              : (formColumnSpan(item.formWidth, wide) ?? '');
            const gap = gapBefore.get(item.id);
            const gapId = gap ? `${gapScope}:${gap.id}` : '';
            return (
              <Fragment key={item.id}>
                {gap ? (
                  <DropGap
                    gapId={gapId}
                    wide={wide}
                    active={overGapId === gapId}
                    onDragOver={(id) => hoverGap(id, extraGroup)}
                    onDrop={(_id, activeId) => dropOnGap(section, gap.beforeId, activeId, extraGroup)}
                  />
                ) : null}
                <DraggableSlot id={item.id} label={label} list={section} span={span} full={item.formWidth === 'full'} {...dragProps}>
                  {field ? (
                    <CustomFieldPreviewField field={field} />
                  ) : isUserFormBuiltin(item.id) ? (
                    <BuiltinPreview id={item.id} roles={roles} />
                  ) : null}
                </DraggableSlot>
              </Fragment>
            );
          })}
          {endGap ? (
            <DropGap
              gapId={`${gapScope}:${endGap.id}`}
              wide={wide}
              active={overGapId === `${gapScope}:${endGap.id}`}
              onDragOver={(id) => hoverGap(id, extraGroup)}
              onDrop={(_id, activeId) => dropOnGap(section, null, activeId, extraGroup)}
            />
          ) : null}
        </div>
      </section>
    );
  }

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex items-start gap-4">
        <HubBackLink to="/campos-personalizados" label="Volver a campos del perfil de usuario" />
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Orden del formulario</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            Suelte un campo sobre otro para ocupar su lugar. Mitad y Largo ajustan el ancho. Los campos personalizados se muestran por grupo. La foto queda al final.
          </p>
        </div>
      </div>

      {error ? (
        <p className="mb-4 text-sm text-error-600" role="alert">
          {(error as Error).message}
        </p>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!dirty) return;
            mutation.mutate();
          }}
        >
          <div className="lms-credentials-glass-panel overflow-hidden rounded-xl shadow-sm">
            {renderSection('personal', 'person', 'Información Personal', 'grid grid-cols-1 gap-6 md:grid-cols-2', 'md')}
            {renderSection('security', 'lock', 'Seguridad', 'grid grid-cols-1 gap-6 md:grid-cols-2', 'md')}
            {renderSection(
              'account',
              'settings_account_box',
              'Configuración de Cuenta',
              'grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12',
              'lg',
            )}
            {extraGroups.length === 0
              ? renderSection('extra', 'edit_note', 'Campos Personalizados', 'grid grid-cols-1 gap-6 lg:grid-cols-2', 'lg')
              : extraGroups.map((group) =>
                  renderSection(
                    'extra',
                    formGroupIcon(group.groupName),
                    group.groupName,
                    'grid grid-cols-1 gap-6 lg:grid-cols-2',
                    'lg',
                    group.items,
                    group.groupName,
                  ),
                )}

            <section className="border-b border-dash-outline-variant p-6 md:p-8">
              <SectionHeading icon="add_a_photo" title="Foto de perfil" />
              <div className="flex flex-col items-center gap-8 md:flex-row">
                <div className="flex h-32 w-32 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-dash-outline-variant bg-secondary-100">
                  <span className="material-symbols-outlined text-4xl text-dash-on-surface-variant" aria-hidden="true">
                    account_circle
                  </span>
                </div>
                <div className="w-full flex-1">
                  <div className="flex flex-col items-center rounded-xl border-2 border-dashed border-dash-outline-variant bg-secondary-50 p-8 text-center">
                    <span className="material-symbols-outlined mb-2 text-3xl text-dash-primary" aria-hidden="true">
                      cloud_upload
                    </span>
                    <p className="mb-1 text-sm font-medium text-dash-on-surface">Haga clic para subir o arrastre y suelte</p>
                    <p className="text-sm text-dash-on-surface-variant">PNG, JPG o WEBP (máx. 2MB)</p>
                  </div>
                </div>
              </div>
            </section>

            <footer className="flex flex-col justify-end gap-4 bg-secondary-50/80 p-6 sm:flex-row md:p-8">
              <Link
                to="/campos-personalizados"
                className="inline-flex items-center justify-center rounded-lg border border-dash-outline-variant px-8 py-3 text-sm font-medium text-dash-primary transition-colors hover:bg-secondary-100"
              >
                Cancelar
              </Link>
              <button
                type="submit"
                disabled={!dirty || mutation.isPending}
                className="inline-flex items-center justify-center rounded-lg bg-dash-primary px-8 py-3 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60"
              >
                {mutation.isPending ? 'Guardando…' : 'Guardar orden'}
              </button>
            </footer>
          </div>
        </form>
      )}
    </div>
  );
}
